import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  Input,
  ChangeDetectorRef,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormGroupDirective,
  Validators,
} from "@angular/forms";
import { MatCardModule } from "@angular/material/card";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { filter, Subject, takeUntil } from "rxjs";
import { Store } from "@ngrx/store";
import { toSignal } from "@angular/core/rxjs-interop";

import {
  selectActivePacket,
  selectTraditionalPacket,
  selectMvpPacket,
  selectMultiPacketMode,
  selectSendingEnvelope,
  selectDrcfSigningError,
} from "@store/selectors/mips-packet/mips-packet.selector";
import { MipsPacketActions } from "@store/actions/mips-packet/mips-packet.actions";
import { DrcfDocument } from "@models/mips-stepper.model";
import { ConfirmationModalComponent } from "@components/confirmation-modal/confirmation-modal.component";
import { EmailInputDialogComponent } from "@components/modals/email-input-dialog/email-input-dialog.component";
import { Actions, ofType } from "@ngrx/effects";
import { getCurrentPerformanceYear, DRCF_SIGNING_ENABLED } from "@constants/mips-constants";

@Component({
  selector: "app-mips-drcf",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: "./mips-drcf.component.html",
  styleUrls: ["./mips-drcf.component.css"],
})
export class MipsDrcfComponent implements OnInit, OnDestroy {
  @Input() parentForm!: FormGroup;
  @Input() isReadOnly = false;

  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly ctrlContainer = inject(FormGroupDirective);
  private readonly dialog = inject(MatDialog);
  private readonly destroy$ = new Subject<void>();

  readonly isDrcfSigningEnabled = DRCF_SIGNING_ENABLED;

  currentPacket = toSignal(this.store.select(selectActivePacket));
  traditionalPacket = toSignal(this.store.select(selectTraditionalPacket));
  mvpPacket = toSignal(this.store.select(selectMvpPacket));
  multiPacketMode = toSignal(this.store.select(selectMultiPacketMode));
  creatingDocument = toSignal(this.store.select(selectSendingEnvelope));

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly actions$ = inject(Actions);

  drcfForm!: FormGroup;
  private hasLoadedEnvelopes = false;
  private loadedPacketIds = new Set<number>();
  private pendingLoads = 0;

  currentTab: "traditional" | "mvp" = "traditional";
  isLoadingEnvelopes = false;
  isCreatingEnvelope = false;
  isVoidingEnvelope = false;
  isDownloadingDocument = false;
  isOpeningDocument = false;
  isDownloadingTemplate = false;

  get showTabs(): boolean {
    return this.multiPacketMode()?.enabled ?? false;
  }

  get currentTabPacket() {
    if (!this.showTabs) {
      return this.currentPacket();
    }
    return this.currentTab === "mvp"
      ? this.mvpPacket()
      : this.traditionalPacket();
  }

  get performanceYear(): number {
    return this.currentTabPacket?.performanceYear || getCurrentPerformanceYear();
  }

  get drcfDocuments(): DrcfDocument[] {
    return this.currentTabPacket?.drcfStatus?.documents || [];
  }

  get allDocumentsSigned(): boolean {
    return this.currentTabPacket?.drcfStatus?.allDocumentsSigned || false;
  }

  get bothPacketsDocumentsSigned(): boolean {
    if (!this.showTabs) return this.allDocumentsSigned;
    const tradSigned = this.traditionalPacket()?.drcfStatus?.allDocumentsSigned || false;
    const mvpSigned = this.mvpPacket()?.drcfStatus?.allDocumentsSigned || false;
    return tradSigned && mvpSigned;
  }

  get hasUnsignedDocuments(): boolean {
    return this.drcfDocuments.some(
      (doc) => doc.status === "sent" || doc.status === "delivered"
    );
  }

  get allDocumentsVoided(): boolean {
    return (
      this.drcfDocuments.length > 0 &&
      this.drcfDocuments.every(
        (doc) =>
          doc.status === "voided" ||
          doc.status === "declined" ||
          doc.status === "timedOut" ||
          doc.status === "authenticationFailed"
      )
    );
  }

  get canSendNewAgreement(): boolean {
    return (
      this.drcfDocuments.length === 0 ||
      this.drcfDocuments.every(
        (doc) =>
          doc.status === "completed" ||
          doc.status === "voided" ||
          doc.status === "declined" ||
          doc.status === "timedOut" ||
          doc.status === "authenticationFailed"
      )
    );
  }

  private isPendingSignature(doc: DrcfDocument): boolean {
    return doc.status === "sent" || doc.status === "delivered";
  }

  get mustVoidFirst(): boolean {
    return this.drcfDocuments.some((doc) => this.isPendingSignature(doc));
  }

  get hasCompletedDocument(): boolean {
    return this.drcfDocuments.some((doc) => doc.status === "completed");
  }

  canDownloadDocument(doc: DrcfDocument): boolean {
    return doc.status === "completed" && doc.hasDownloadableDocument;
  }

  isDocumentProcessing(doc: DrcfDocument): boolean {
    return doc.status === "completed" && !doc.hasDownloadableDocument;
  }

  ngOnInit(): void {
    this.drcfForm = this.fb.group({
      allDocumentsSigned: [false],
    });

    setTimeout(() => {
      this.parentForm = this.ctrlContainer.form;
      if (this.parentForm) {
        this.parentForm.addControl("drcf", this.drcfForm);
      }
    });

    this.store
      .select(selectActivePacket)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadEnvelopesForPackets();
        this.updateParentFormValidity();
      });

    this.store
      .select(selectSendingEnvelope)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isSending) => {
        this.isCreatingEnvelope = isSending;
      });

    this.actions$
      .pipe(
        ofType(MipsPacketActions.openDRCFSigningSuccess),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isOpeningDocument = false;
        this.cdr.detectChanges();
      });

    this.actions$
      .pipe(
        ofType(MipsPacketActions.loadDRCFEnvelopesSuccess),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.pendingLoads = Math.max(0, this.pendingLoads - 1);
        if (this.pendingLoads === 0) {
          this.isLoadingEnvelopes = false;
        }
        this.isVoidingEnvelope = false;
        this.updateParentFormValidity();
        this.cdr.detectChanges();
      });

    this.actions$
      .pipe(
        ofType(MipsPacketActions.voidDRCFEnvelopeFailure),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isVoidingEnvelope = false;
        this.cdr.detectChanges();
      });

    this.actions$
      .pipe(
        ofType(MipsPacketActions.downloadDRCFTemplateSuccess),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isDownloadingTemplate = false;
        this.cdr.detectChanges();
      });

    this.actions$
      .pipe(
        ofType(MipsPacketActions.downloadDRCFTemplateFailure),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isDownloadingTemplate = false;
        this.cdr.detectChanges();
      });

    this.store
      .select(selectDrcfSigningError)
      .pipe(
        takeUntil(this.destroy$),
        filter((error) => error != null)
      )
      .subscribe((error) => {
        
        this.isOpeningDocument = false;
        this.isLoadingEnvelopes = false;
        this.isCreatingEnvelope = false;
        this.isVoidingEnvelope = false;
        this.isDownloadingDocument = false;

        this.cdr.detectChanges();

        const isValidationError =
          error.includes("not authorized") || error.includes("assigned to");

        const dialogRef = this.dialog.open(ConfirmationModalComponent, {
          width: "500px",
          disableClose: true,
          data: {
            title: isValidationError
              ? "⚠️ Authorization Required"
              : "Unable to Open Document",
            content: error,
            confirmText: "OK",
            isError: true,
            severity: isValidationError ? "warning" : "error",
          },
        });

        dialogRef.afterClosed().subscribe(() => {
          this.store.dispatch(MipsPacketActions.clearError());
          this.isOpeningDocument = false;
          this.cdr.detectChanges();
        });
      });
  }

  ngOnDestroy(): void {
    if (this.parentForm) {
      this.parentForm.removeControl("drcf");
    }
    this.hasLoadedEnvelopes = false;
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateParentFormValidity(): void {
    if (!this.parentForm) return;

    const allSigned = this.bothPacketsDocumentsSigned;

    this.drcfForm.patchValue(
      { allDocumentsSigned: allSigned },
      { emitEvent: false }
    );

    this.parentForm.setErrors(null);
  }

  private loadEnvelopesForPackets(): void {
    const packets = this.showTabs
      ? [this.traditionalPacket(), this.mvpPacket()]
      : [this.currentPacket()];

    for (const packet of packets) {
      if (!packet?.id) continue;
      if (this.loadedPacketIds.has(packet.id)) {
        if (packet.drcfStatus?.documents !== undefined) {
          this.isLoadingEnvelopes = false;
        }
        continue;
      }

      if (!packet.drcfStatus?.documents) {
        this.pendingLoads++;
        this.isLoadingEnvelopes = true;
        this.store.dispatch(
          MipsPacketActions.loadDRCFEnvelopes({ submissionId: packet.id })
        );
      }
      this.loadedPacketIds.add(packet.id);
    }

    const currentTabPkt = this.currentTabPacket;
    if (currentTabPkt?.drcfStatus?.documents !== undefined) {
      this.isLoadingEnvelopes = false;
      this.hasLoadedEnvelopes = true;
    }
  }

  onTabChange(tab: "traditional" | "mvp"): void {
    this.currentTab = tab;
  }

  private isTerminalStatus(doc: DrcfDocument): boolean {
    return (
      doc.status === "completed" ||
      doc.status === "voided" ||
      doc.status === "declined" ||
      doc.status === "timedOut" ||
      doc.status === "authenticationFailed"
    );
  }

  createDrcfEnvelope(): void {
    const submissionId = this.currentTabPacket?.id;

    if (!submissionId) return;

    const hasExistingDocument = this.drcfDocuments.some(
      (doc) => this.isTerminalStatus(doc)
    );

    if (hasExistingDocument) {
      const dialogRef = this.dialog.open(ConfirmationModalComponent, {
        data: {
          title: "Replace Existing Agreement",
          content:
            "Creating a new DRCF agreement will permanently delete the existing agreement. This action cannot be undone. Are you sure you want to continue?",
          confirmText: "Create New Agreement",
          cancelText: "Cancel",
        },
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.isCreatingEnvelope = true;
          this.store.dispatch(
            MipsPacketActions.createDRCFEnvelope({
              submissionId,
              recipientEmail: null,
            })
          );
        }
      });
    } else {
      this.isCreatingEnvelope = true;
      this.store.dispatch(
        MipsPacketActions.createDRCFEnvelope({
          submissionId,
          recipientEmail: null,
        })
      );
    }
  }

  createDrcfEnvelopeWithEmail(): void {
    const submissionId = this.currentTabPacket?.id;
    if (!submissionId) return;

    const hasExistingDocument = this.drcfDocuments.some(
      (doc) => this.isTerminalStatus(doc)
    );

    if (hasExistingDocument) {
      const confirmDialogRef = this.dialog.open(ConfirmationModalComponent, {
        data: {
          title: "Replace Existing Agreement",
          content:
            "Creating a new DRCF agreement will permanently delete the existing agreement. This action cannot be undone. Are you sure you want to continue?",
          confirmText: "Continue",
          cancelText: "Cancel",
        },
      });

      confirmDialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.openEmailInputDialog(submissionId);
        }
      });
    } else {
      this.openEmailInputDialog(submissionId);
    }
  }

  private openEmailInputDialog(submissionId: number): void {
    const emailDialogRef = this.dialog.open(EmailInputDialogComponent, {
      width: "400px",
      data: {
        title: "Send Agreement via Email",
        message:
          "Enter the email address where the DRCF agreement should be sent:",
      },
    });

    emailDialogRef.afterClosed().subscribe((result: { email: string; name: string } | null) => {
      if (result?.email) {
        this.isCreatingEnvelope = true;
        this.store.dispatch(
          MipsPacketActions.createDRCFEnvelope({
            submissionId,
            recipientEmail: result.email,
          })
        );
      }
    });
  }

  openDocument(envelopeId: string): void {
    this.isOpeningDocument = true;
    this.store.dispatch(MipsPacketActions.openDRCFSigning({ envelopeId }));
  }

  downloadDocument(envelopeId: string): void {
  const submissionId = this.currentTabPacket?.id;
  if (submissionId) {
    this.store.dispatch(
      MipsPacketActions.downloadDRCFDocument({ submissionId, envelopeId })
    );
    

  }
}

  downloadDrcfTemplate(): void {
    const entityType = this.currentTabPacket?.reportingLevel || "individual";
    this.isDownloadingTemplate = true;
    this.store.dispatch(MipsPacketActions.downloadDRCFTemplate({ entityType }));
  }

  voidDocument(envelopeId: string): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        title: "Void DRCF Agreement",
        content:
          "Are you sure you want to void this agreement? This action cannot be undone.",
        confirmText: "Void Agreement",
        cancelText: "Cancel",
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const submissionId = this.currentTabPacket?.id;
        if (submissionId) {
          this.isVoidingEnvelope = true;
          this.store.dispatch(
            MipsPacketActions.voidDRCFEnvelope({
              submissionId,
              envelopeId,
              voidReason: "User requested void",
            })
          );
        }
      }
    });
  }

  refreshDocuments(): void {
    const submissionId = this.currentTabPacket?.id;
    if (submissionId) {
      this.pendingLoads++;
      this.isLoadingEnvelopes = true;
      this.store.dispatch(
        MipsPacketActions.loadDRCFEnvelopes({ submissionId })
      );
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case "completed":
        return "green";
      case "sent":
      case "delivered":
        return "orange";
      case "voided":
      case "declined":
      case "timedOut":
      case "authenticationFailed":
        return "red";
      default:
        return "gray";
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case "completed":
        return "Signed";
      case "sent":
      case "delivered":
        return "Needs to be Signed";
      case "voided":
        return "Voided";
      case "declined":
        return "Declined";
      case "timedOut":
        return "Expired";
      case "authenticationFailed":
        return "Authentication Failed";
      default:
        return status;
    }
  }
}
